import React, { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import CinemaIcon from "../cinema/CinemaIcon";
import localizationStrings from "../../statics/localization";
import "./LogoutConfirmation.scss";

const translations = {
  eng: {
    title: "Sign out of Atlas?",
    description:
      "Your next story will be here. Sign back in whenever you’re ready.",
    cancel: "Stay here",
    confirm: "Sign out",
    close: "Close"
  },
  tr: {
    title: "Atlas’tan çıkış yapılsın mı?",
    description:
      "Sıradaki hikâyen burada seni bekliyor. Dilediğin zaman yeniden giriş yapabilirsin.",
    cancel: "Burada kal",
    confirm: "Çıkış yap",
    close: "Kapat"
  },
  arb: {
    title: "تسجيل الخروج من أطلس؟",
    description: "قصتك التالية بانتظارك. سجّل الدخول مجدداً متى شئت.",
    cancel: "البقاء هنا",
    confirm: "تسجيل الخروج",
    close: "إغلاق"
  }
};

export default function LogoutConfirmation({
  onCancel,
  onConfirm,
  returnFocusRef
}) {
  const language = localizationStrings.getLanguage();
  const copy = translations[language] || translations.eng;
  const dialogRef = useRef(null);
  const cancelRef = useRef(null);
  const cancelAction = useRef(onCancel);
  const confirmed = useRef(false);
  cancelAction.current = onCancel;

  useEffect(() => {
    const opener =
      (returnFocusRef && returnFocusRef.current) || document.activeElement;
    const root = document.getElementById("root");
    const wasInert = root && root.hasAttribute("inert");
    const x = window.scrollX,
      y = window.scrollY;
    const style = document.body.style;
    const previous = {};
    [
      "position",
      "top",
      "left",
      "right",
      "width",
      "overflow",
      "paddingRight"
    ].forEach((key) => {
      previous[key] = style[key];
    });
    const gutter = window.innerWidth - document.documentElement.clientWidth;
    style.position = "fixed";
    style.top = `-${y}px`;
    style.left = "0";
    style.right = "0";
    style.width = "100%";
    style.overflow = "hidden";
    if (gutter > 0) style.paddingRight = `${gutter}px`;
    document.body.classList.add("atlas-logout-open");
    if (cancelRef.current) cancelRef.current.focus({ preventScroll: true });
    if (root) root.setAttribute("inert", "");

    const onKeyDown = (event) => {
      if (event.defaultPrevented) return;
      if (event.key === "Escape") {
        event.preventDefault();
        event.stopPropagation();
        cancelAction.current();
      }
      if (event.key !== "Tab" || !dialogRef.current) return;
      const buttons = dialogRef.current.querySelectorAll(
        "button:not([disabled])"
      );
      const first = buttons[0],
        last = buttons[buttons.length - 1];
      const active = document.activeElement;
      if (
        !dialogRef.current.contains(active) ||
        (event.shiftKey && active === first)
      ) {
        event.preventDefault();
        (event.shiftKey ? last : first).focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", onKeyDown, true);
    return () => {
      document.removeEventListener("keydown", onKeyDown, true);
      if (root && !wasInert) root.removeAttribute("inert");
      Object.keys(previous).forEach((key) => {
        style[key] = previous[key];
      });
      document.body.classList.remove("atlas-logout-open");
      window.scrollTo(x, y);
      if (opener && opener.isConnected) opener.focus({ preventScroll: true });
    };
  }, [returnFocusRef]);

  return createPortal(
    <div
      className="atlas-logout-overlay"
      dir={language === "arb" ? "rtl" : "ltr"}
      onClick={(event) => {
        if (event.target === event.currentTarget) onCancel();
      }}
    >
      <section
        className="atlas-logout-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="atlas-logout-title"
        aria-describedby="atlas-logout-description"
        ref={dialogRef}
      >
        <button
          type="button"
          className="atlas-logout-close"
          aria-label={copy.close}
          onClick={onCancel}
        >
          <CinemaIcon name="close" />
        </button>
        <div className="atlas-logout-symbol" aria-hidden="true">
          <svg
            width="28"
            height="28"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M10 4H5a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h5M9 12h12m-4-4 4 4-4 4" />
          </svg>
        </div>
        <p className="atlas-logout-eyebrow">ATLAS</p>
        <h2 id="atlas-logout-title">{copy.title}</h2>
        <p id="atlas-logout-description">{copy.description}</p>
        <div className="atlas-logout-actions">
          <button
            type="button"
            className="atlas-logout-cancel"
            onClick={onCancel}
            ref={cancelRef}
          >
            {copy.cancel}
          </button>
          <button
            type="button"
            className="atlas-logout-confirm"
            onClick={() => {
              if (confirmed.current) return;
              confirmed.current = true;
              onConfirm();
            }}
          >
            {copy.confirm}
          </button>
        </div>
      </section>
    </div>,
    document.getElementById("notification_root") || document.body
  );
}
